<template>
  <BaseModal ref="modalRef" class="max-w-[500px]!" @close="resetForm" :has-back-navigation="step !== 'CREATE_ACCOUNT'" @back="previousStep()">
    <form v-if="step === 'CREATE_ACCOUNT'" action="" class="bounce-in" @submit.prevent="completeStep()">
      <div class="">
        <div class="mb-8 flex flex-col gap-y-2 items-center">
          <h2 class="text-2xl text-center font-semibold text-white">Create an account</h2>
        </div>
        <div class="grid grid-cols-2 gap-x-6 gap-y-5 mb-5">
          <div class="input-group">
            <div class="input-label">First name</div>
            <input v-model="firstName" type="text" class="input-item w-full" />

            <template v-if="$v.firstName.$dirty">
              <span v-for="error in $v.firstName.$errors" :key="error.$uid" class="input-error">
                {{ error.$message }}
              </span>
            </template>
          </div>
          <div class="input-group">
            <div class="input-label">Last name</div>
            <input v-model="lastName" type="text" class="input-item w-full" />

            <template v-if="$v.lastName.$dirty">
              <span v-for="error in $v.lastName.$errors" :key="error.$uid" class="input-error">
                {{ error.$message }}
              </span>
            </template>
          </div>
        </div>
        <div class="grid grid-cols-1 gap-y-5">
          <div class="input-group">
            <div class="input-label">Date of Birth</div>
            <Datepicker
              v-model="dateOfBirth"
              model-type="yyyy-MM-dd"
              :max-date="fiveYearsAgo"
              :start-date="fiveYearsAgo"
              focus-start-date
              :format="'yyyy-MM-dd'"
              :enable-time-picker="false"
              auto-apply
              :teleport-center="useHelpers().isMobileDevice()"
              :input-class="'w-full py-4 border-b border-blue-light placeholder:text-blue-light text-blue-te focus:outline-none focus:ring-blue-500 focus:border-primary'"
              name="dob"
              autocomplete="off"
            />

            <template v-if="$v.dateOfBirth.$dirty">
              <span v-for="error in $v.dateOfBirth.$errors" :key="error.$uid" class="input-error">
                {{ error.$message }}
              </span>
            </template>
          </div>
          <p v-if="errorMessage?.toLowerCase()?.includes('first name') || errorMessage?.toLowerCase()?.includes('last name') || errorMessage?.toLowerCase()?.includes('date of birth')" class="text-cw-red">
            {{ errorMessage }}
          </p>
        </div>
        <div class="w-max ml-auto mt-4">
          <BaseButton background-color="#1AA570" border-color="var(--color-cw-green-border-shadow)" class="px-10 py-3.5 ml-auto mt-8" :disabled="isLoading">
            <Loader v-if="isLoading" />
            <p class="!text-base xl:!text-xl">Continue</p>
          </BaseButton>
        </div>
      </div>
    </form>
    <form v-else-if="step === 'COMPLETE_PROFILE'" class="bounce-in" action="" @submit.prevent="completeStep()">
      <div class="">
        <div class="mb-8 flex flex-col gap-y-2 items-center">
          <h2 class="text-2xl text-center font-semibold text-white">Complete your profile</h2>
        </div>
        <div class="grid grid-cols-1 gap-y-5">
          <div class="input-group">
            <Carousel v-bind="carouselConfig" v-model="avatar">
              <Slide v-for="(avatarOption, index) in avatarOptions" :key="avatarOption">
                <img :src="avatarOption" :class="['w-24 h-24 rounded-2xl cursor-pointer border-4 border-[#082552]']" @click="selectAvatar(index)" />
              </Slide>
            </Carousel>
            <div class="input-label text-center text-[#4D4D4D] mt-2">Swipe to choose an avatar</div>
          </div>
          <div class="input-group">
            <div class="input-label">Choose a username</div>
            <input v-model="username" type="text" class="input-item w-full" placeholder="e.g lamine_yamal" />

            <template v-if="$v.username.$dirty">
              <span v-for="error in $v.username.$errors" :key="error.$uid" class="input-error">
                {{ error.$message }}
              </span>
            </template>
          </div>
          <p v-if="errorMessage?.toLowerCase()?.includes('username')" class="text-cw-red">{{ errorMessage }}</p>
        </div>
        <div class="w-max ml-auto mt-4">
          <BaseButton background-color="#1AA570" border-color="var(--color-cw-green-border-shadow)" class="px-10 py-3.5 ml-auto mt-8" :disabled="isLoading">
            <Loader v-if="isLoading" />
            <p class="!text-base xl:!text-xl">Continue</p>
          </BaseButton>
        </div>
      </div>
    </form>
    <form v-else-if="step === 'SET_PASSWORD'" class="bounce-in" action="" @submit.prevent="completeStep()">
      <div class="">
        <div class="mb-8 flex flex-col gap-y-2 items-center">
          <PadlockIcon />
          <h2 class="text-2xl text-center font-semibold text-white">Set your password</h2>
        </div>
        <div class="grid grid-cols-1 gap-y-5">
          <BasePasswordInput v-model="password" label="Password">
            <template #errors>
              <template v-if="$v.password.$dirty">
                <span v-for="error in $v.password.$errors" :key="error.$uid" class="input-error">
                  {{ error.$message }}
                </span>
              </template>
            </template>
          </BasePasswordInput>
          <p v-if="errorMessage?.toLowerCase()?.includes('password')" class="text-cw-red">{{ errorMessage }}</p>
        </div>
        <div class="w-max ml-auto mt-4">
          <BaseButton background-color="#1AA570" border-color="var(--color-cw-green-border-shadow)" class="px-10 py-3.5 ml-auto mt-8" :disabled="isLoading">
            <Loader v-if="isLoading" />
            <p class="!text-base xl:!text-xl">Set password</p>
          </BaseButton>
        </div>
      </div>
    </form>
    <form v-else-if="step === 'MORE_INFO'" class="bounce-in" action="" @submit.prevent="completeStep()">
      <div class="">
        <div class="mb-8 flex flex-col gap-y-2 items-center">
          <h2 class="text-2xl text-center font-semibold text-white">Tell us more about you</h2>
        </div>
        <div class="grid grid-cols-1 gap-y-5">
          <div class="input-group">
            <div class="input-label">School name</div>
            <select v-model="school" class="input-item w-full" placeholder="Select your school">
              <option :value="school.id" :key="school.id" v-for="school in schools">{{ school.name }}</option>
              <option :value="null">Does not apply</option>
            </select>
          </div>
          <div class="input-group">
            <div class="input-label">Email address</div>
            <input v-model="email" type="text" class="input-item w-full" placeholder="" />

            <template v-if="$v.email.$dirty">
              <span v-for="error in $v.email.$errors" :key="error.$uid" class="input-error">
                {{ error.$message }}
              </span>
            </template>
          </div>
        </div>
        <div class="w-full flex items-center mt-8 justify-end">
          <a href="" @click.prevent="completeStep()" class="inline-flex items-center mr-4 text-base text-cw-green" v-if="!email && !school">
            Skip for now
            <ArrowSlideRightIcon class="w-auto h-4 ml-2" />
          </a>
          <BaseButton background-color="#1AA570" border-color="var(--color-cw-green-border-shadow)" class="px-10 py-3.5" :disabled="isLoading">
            <Loader v-if="isLoading" />
            <p class="!text-base xl:!text-xl">Continue</p>
          </BaseButton>
        </div>
      </div>
    </form>
  </BaseModal>
</template>

<script setup lang="ts">
import { useTemplateRef, ref, computed } from 'vue'
import BaseModal from '@/components/base/BaseModal.vue'
import BasePasswordInput from '@/components/base/BasePasswordInput.vue'
import Loader from '@/components/interface/Loader.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useRouter } from 'vue-router'
import { useHelpers } from '@/composables/useHelpers'
import AuthService from '@/services/api/auth-service'
import PadlockIcon from '@/assets/svgs/icons/padlock.svg'
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import { useUserStore } from '@/stores/user'
import { Carousel, Slide } from 'vue3-carousel'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import 'vue3-carousel/carousel.css'
import { useUser } from '@/composables/useUser'
import ArrowSlideRightIcon from '@/assets/svgs/icons/arrow-slide-right.svg'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores/app'

const { push } = useRouter()
const { setCookie, sleep } = useHelpers()
const { setUser } = useUserStore()
const { fetchAllSchools } = useUser()
fetchAllSchools()
const { schools } = storeToRefs(useAppStore())

const modalRef = useTemplateRef('modalRef')
const step = ref<'CREATE_ACCOUNT' | 'COMPLETE_PROFILE' | 'SET_PASSWORD' | 'MORE_INFO'>('CREATE_ACCOUNT')
const avatar = ref(0)
const email = ref('')
const password = ref('')
const username = ref('')
const school = ref('')
const firstName = ref('')
const lastName = ref('')
const dateOfBirth = ref('')
const errorMessage = ref('')
const isLoading = ref(false)

const fiveYearsAgo = computed(() => {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 5)
  return date
})

const $v = useVuelidate(
  {
    firstName: {
      required: helpers.withMessage('Enter your first name', required),
      $autoDirty: true
    },
    lastName: {
      required: helpers.withMessage('Enter your last name', required),
      $autoDirty: true
    },
    dateOfBirth: {
      required: helpers.withMessage('Enter your date of birth', required),
      $autoDirty: true
    },
    avatar: {
      required: helpers.withMessage('Select your avatar', required)
    },
    password: {
      required: helpers.withMessage('Enter your password', required),
      $autoDirty: true
    },
    username: {
      required: helpers.withMessage('Enter your username', required),
      $autoDirty: true
    },
    email: {
      email: helpers.withMessage('Enter a valid email address', (value: string) => {
        if (!value) return true // email is not required
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(value)
      })
    },
    school: {}
  },
  { avatar, password, username, firstName, lastName, dateOfBirth, email, school }
)

const carouselConfig = {
  itemsToShow: 3.5,
  wrapAround: true
}
const avatarOptions = [
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-g.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-h.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-i.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-j.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-k.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-a.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-b.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-c.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-d.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-e.svg?alt=media',
  'https://firebasestorage.googleapis.com/v0/b/cowrywise.appspot.com/o/NestKids%2Favatars%2Favatar-f.svg?alt=media'
]

const open = () => {
  modalRef.value?.open()
}

const close = () => {
  modalRef.value?.close()
}

const resetForm = async () => {
  await sleep(200)
  email.value = ''
  school.value = ''
  password.value = ''
  firstName.value = ''
  lastName.value = ''
  dateOfBirth.value = ''
  avatar.value = 0
  errorMessage.value = ''
  step.value = 'CREATE_ACCOUNT'
  $v.value.$reset()
}

const selectAvatar = (index: number) => {
  avatar.value = index
}

const createAccount = async () => {
  const isValid = await $v.value.$validate()
  if (!isValid) return
  try {
    isLoading.value = true
    const { access_token, user } = await AuthService.createAccount({
      password: password.value,
      username: username.value,
      firstName: firstName.value,
      lastName: lastName.value,
      dateOfBirth: dateOfBirth.value,
      avatarUrl: avatarOptions[avatar.value],
      email: email.value,
      schoolId: school.value
    })
    setCookie('access_token', access_token, 1)
    setUser(user)
    errorMessage.value = ''
    close()
    await sleep(200)
    push('/')
  } catch (error: ApiError) {
    errorMessage.value = typeof error.data?.message === 'string' ? error.data?.message || 'An error occurred while logging in.' : error.data?.message[0]
    if (errorMessage.value?.toLowerCase()?.includes('password')) {
      step.value = 'SET_PASSWORD'
    } else if (errorMessage.value?.toLowerCase()?.includes('username')) {
      step.value = 'COMPLETE_PROFILE'
    } else {
      step.value = 'CREATE_ACCOUNT'
    }
    isLoading.value = false
  } finally {
    // console.log('finally')
    isLoading.value = false
  }
}

const completeStep = async () => {
  let isValid = false
  if (step.value === 'CREATE_ACCOUNT') {
    isValid = (await $v.value.dateOfBirth?.$validate()) && (await $v.value.firstName?.$validate()) && (await $v.value.lastName?.$validate())
    if (!isValid) return
    step.value = 'COMPLETE_PROFILE'
  } else if (step.value === 'COMPLETE_PROFILE') {
    isValid = await $v.value.username?.$validate()
    if (!isValid) return
    step.value = 'SET_PASSWORD'
  } else if (step.value === 'SET_PASSWORD') {
    isValid = await $v.value.password?.$validate()
    if (!isValid) return
    step.value = 'MORE_INFO'
  } else if (step.value === 'MORE_INFO') {
    isValid = await $v.value.email?.$validate()
    if (!isValid) return
    createAccount()
  }
}

const previousStep = () => {
  if (step.value === 'COMPLETE_PROFILE') {
    step.value = 'CREATE_ACCOUNT'
  } else if (step.value === 'SET_PASSWORD') {
    step.value = 'COMPLETE_PROFILE'
  } else if (step.value === 'MORE_INFO') {
    step.value = 'SET_PASSWORD'
  }
}

defineExpose({
  open,
  close
})
</script>
